/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    Alert, Badge, Box, Button, Chip, Divider, IconButton, MenuItem, Paper, Popover,
    Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Iconify } from 'src/components';
import { callApi } from 'src/utils/apiCall';
import { clearWhatsappAiStatus } from 'src/redux/slices/whatsappV2';
// Shared with the super-admin Activity tab deliberately. The verdict/tag
// interaction in that component is where every subtle bug in this feature has
// lived; a second copy for this screen would drift from it on the first change.
import TurnRating from 'src/pages/superAdmin/whatsappAiBot/TurnRating';

const base = (practiceId) => `/api/v1/practices/${practiceId}/whatsapp-ai/threads`;

// INHERIT is offered as "Practice default" rather than hidden: an operator
// needs a way back to following the practice after overriding a thread.
const MODES = [
    {
        value: 'INHERIT',
        label: 'Practice default',
        icon: 'solar:buildings-2-bold-duotone',
        help: 'Do whatever the practice is set to.',
    },
    {
        value: 'PAUSED',
        label: 'Paused',
        icon: 'solar:pause-bold',
        help: 'The AI stays out of this conversation entirely.',
    },
    {
        value: 'DRAFT',
        label: 'Draft for me',
        icon: 'solar:pen-new-square-bold-duotone',
        help: 'It writes the reply. Nothing sends until you press Send.',
    },
    {
        value: 'AUTO',
        label: 'Auto-send',
        icon: 'solar:bolt-bold-duotone',
        help: 'It replies to the patient on its own.',
    },
];

const EFFECTIVE = {
    OFF: { text: 'Off here', colour: 'default', dot: null },
    // Unreachable from the inbox by design: the server presents SHADOW as OFF
    // to every caller, superusers included, and this component renders nothing
    // for OFF. Kept so the map stays exhaustive if that ever changes.
    SHADOW: { text: 'Shadow — nothing sends', colour: 'default', dot: 'info' },
    DRAFT: { text: 'Drafting for you', colour: 'info', dot: 'info' },
    LIVE: { text: 'Replying automatically', colour: 'success', dot: 'success' },
};

/** What will ACTUALLY happen on this conversation, in one line.
 *
 *  The header used to show the configured mode and the exceptions as separate
 *  chips beside it, which produced "Replying automatically" and "Handed to you"
 *  sitting next to each other — two badges flatly contradicting one another,
 *  with the true one the smaller of the two. A handoff, a staff pause and a
 *  closed conversation all stop the AI replying no matter what mode says, so
 *  they are not decorations on the mode: they replace it.
 *
 *  Ordered by what overrides what, most restrictive first. The mode is the
 *  fallback, not the headline.
 */
const activityOf = (state) => {
    if (state.conversation_state === 'HANDOFF') {
        return {
            text: 'Waiting for you',
            colour: 'warning',
            dot: 'warning',
            help: 'The AI stopped and passed this to you. It will not reply again until you hand it back.',
        };
    }
    if (state.conversation_state === 'CLOSED') {
        return {
            text: 'Conversation closed',
            colour: 'default',
            dot: null,
            help: 'This conversation is finished. The AI will not reply unless the patient starts a new one.',
        };
    }
    if (state.conversation_state === 'PAUSED_BY_STAFF' || state.ai_mode === 'PAUSED') {
        return {
            text: 'Paused here',
            colour: 'default',
            dot: null,
            help: 'The AI is staying out of this conversation. Everything else about the practice is unchanged.',
        };
    }
    const base = EFFECTIVE[state.effective_mode] || EFFECTIVE.OFF;
    return {
        ...base,
        help: {
            OFF: 'The AI is not handling this conversation.',
            SHADOW: 'The AI is working out what it would say, and recording it. Nothing reaches the patient.',
            DRAFT: 'The AI writes each reply and leaves it here for you. Nothing sends until you press Send.',
            LIVE: 'The AI is answering this patient on its own.',
        }[state.effective_mode] || '',
    };
};

/**
 * The AI bot control for one conversation.
 *
 * This used to be a full-width bar under the chat header. It cost a permanent
 * strip of vertical space on every conversation to show a setting almost nobody
 * changes, and it did not match the icon toolbar sitting immediately above it.
 * It is now a toolbar button that opens on demand, and it renders nothing at
 * all unless the AI is switched on for the practice — so practices that have
 * not enabled it see no change anywhere in the inbox.
 */
/** Stamp arrival on a live status, so the countdown has something to subtract.
 *
 *  The server sends a remaining-seconds figure with no timestamp. Only `load`
 *  stamped it, so the responses from set-mode, send-draft and discard-draft
 *  arrived without one — and the countdown then treated elapsed as zero and
 *  re-rendered the same number once a second forever. Switching a thread to
 *  Draft froze the chip at "AI will reply in 85s" until the operator left it.
 */
const stamp = (next) => {
    if (next?.live_status) {
        return {
            ...next,
            live_status: { ...next.live_status, received_at: Date.now() },
        };
    }
    return next;
};

export default function WhatsappAIModeControl({ practiceId, threadId, iconSx, onDraftState }) {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);
    const [draftText, setDraftText] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [error, setError] = useState('');
    // The thread this component is currently showing, readable from inside an
    // async callback that closed over an older one.
    const threadIdRef = useRef(threadId);
    // Bumped to remount TurnRating when the reply underneath it is replaced, so
    // no tag or half-typed comment carries from one reply to the next.
    const [ratingKey, setRatingKey] = useState(0);
    const dispatch = useDispatch();

    // Live status arrives on the socket. On a page load there is nothing in the
    // store yet, so the thread endpoint's derived status stands in until the
    // first event — the socket is the fast path, not the only one.
    const socketStatus = useSelector(
        (s) => s?.whatsappV2?.whatsappAiStatusByThread?.[threadId],
    );

    // Once ANY socket event has arrived for this thread, the mount-time snapshot
    // is dead to us. Without this, a `resolved` event deletes the store entry and
    // `live` falls straight back to the stale snapshot — so finishing a reply made
    // the chip revert to "AI will reply in 90s" and stay there. The resolve
    // actively made the display worse than never receiving it.
    const [socketSeen, setSocketSeen] = useState(false);
    useEffect(() => {
        if (socketStatus) setSocketSeen(true);
    }, [socketStatus]);
    // Keyed on threadId, and it re-seeds from the store rather than resetting to
    // false. Two effects with different keys ran in declaration order on a
    // thread switch, so the reset won and the set did not re-run — the latch was
    // off even though a socket entry existed, and the next event for that
    // thread is the `resolved` that DELETES the entry, which is falsy. So the
    // stale mount-time snapshot came back and the chip reverted to "AI will
    // reply in 90s" on a thread that had already been answered: the exact
    // regression the latch was added to prevent, reached by clicking between
    // conversations.
    // threadId only. Re-running this on every socketStatus change is what the
    // effect above already does; adding it here would defeat the latch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { setSocketSeen(!!socketStatus); }, [threadId]);

    const live = socketStatus || (socketSeen ? null : state?.live_status) || null;

    const load = useCallback(async () => {
        if (!threadId || !practiceId) return;
        // Which thread this load is for. The component is mounted without a key
        // (see ChatPage), so switching conversations is a prop change, not a
        // remount: two loads can be in flight and the slower one used to win.
        // That put the previous patient's draft in the popover on the next
        // patient's conversation — and pressing Send posted that turn id to this
        // thread, which 404s and was swallowed, so the box emptied and the
        // operator could not tell whether the message had gone.
        const wanted = threadId;
        const res = await callApi(`${base(practiceId)}/${threadId}/`);
        if (wanted !== threadIdRef.current) return;
        if (res?.ok) {
            setState(stamp(res.data));
            return;
        }
        // 404 is "not configured for this practice" and showing nothing is
        // right. A 5xx or a network failure is a broken endpoint, and blanking
        // the control took the mode selector, the pause switch and the handoff
        // button off the screen along with it — so keep what we have.
        if (res?.status >= 500 || res?.status === 0) {
            // eslint-disable-next-line no-console
            console.error('WhatsApp AI: thread state failed', threadId, res?.error);
            return;
        }
        setState(null);
    }, [threadId, practiceId]);

    // Nothing from the previous conversation may survive a thread switch, and
    // the switch is a prop change rather than a remount.
    useEffect(() => {
        threadIdRef.current = threadId;
        setState(null);
        setDraftText('');
        setError('');
    }, [threadId]);

    useEffect(() => { load(); }, [load]);

    // A draft landing on the socket is the one event that changes data rather
    // than just the indicator, so refetch to pick up the turn id the Send button
    // needs, then drop the transient status.
    useEffect(() => {
        if (socketStatus?.state === 'drafted') {
            load();
            dispatch(clearWhatsappAiStatus(threadId));
        }
    }, [socketStatus?.state, socketStatus?.turn_id, threadId, load, dispatch]);

    // Count the queued window down locally rather than asking the server every
    // second. Purely cosmetic: the real decision is made by the gate.
    const [tick, setTick] = useState(0);
    useEffect(() => {
        if (live?.state !== 'queued') return undefined;
        const id = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(id);
    }, [live?.state, live?.received_at]);

    const countdown = useMemo(() => {
        if (live?.state !== 'queued') return null;
        const started = live.received_at ? (Date.now() - live.received_at) / 1000 : 0;
        return Math.max(0, Math.round((live.countdown_seconds || 0) - started));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [live?.state, live?.countdown_seconds, live?.received_at, tick]);

    // Re-seed the editable box whenever a new draft arrives, but never clobber
    // edits already in progress on the same draft. Keyed on the draft ID, not
    // its text: re-running on text change would overwrite an operator mid-edit.
    useEffect(() => {
        setDraftText(state?.pending_draft?.text || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state?.pending_draft?.id]);

    const resolveDraft = async (path, overrideText) => {
        if (!state?.pending_draft) return false;
        setBusy(true);
        try {
            const res = await callApi(`${base(practiceId)}/${threadId}/${path}/`, 'POST', {
                turn_id: state.pending_draft.id,
                // The composer is the editing surface now, so the text that
                // goes is whatever is in the box down there — not the copy this
                // component happens to be holding.
                text: overrideText === undefined ? draftText : overrideText,
            });
            if (res?.ok) {
                setError('');
                setState(stamp(res.data));
                return true;
            }
            // Say so. Swallowing this left an operator who had pressed Send
            // looking at an empty box with no idea whether the patient had been
            // messaged.
            setError(path === 'send-draft'
                ? 'That draft could not be sent. Nothing has gone to the patient.'
                : 'That draft could not be discarded.');
            load();
            return false;
        } finally {
            setBusy(false);
        }
    };

    /** Record a verdict on the reply the server is currently offering.
     *
     *  Nothing is disabled while this is in flight — see TurnRating, where
     *  disabling on a busy flag ate the click that flipped a verdict. Every
     *  payload carries the whole state, so overlapping saves are
     *  last-write-wins, and a response for a thread we have since left is
     *  discarded rather than painted onto the new one.
     */
    const rate = async (payload) => {
        // The turn id comes from the BUBBLE, not from the thread.
        //
        // This used to read `state.rateable` and return early when it was
        // missing — the shape of the old popover, which rated whatever the
        // thread's newest reply happened to be. The thumbs have lived on each
        // AI message since, and `rateable` is null whenever the practice is not
        // LIVE, so every click in a shadow practice hit `if (!rateable) return`
        // and fired no request at all. Nothing happened, and nothing said so.
        const turnId = payload?.turn_id ?? state?.rateable?.turn_id ?? null;
        if (!turnId || !threadId || !practiceId) return { ok: false };
        const wanted = threadId;
        const res = await callApi(
            `${base(practiceId)}/${threadId}/rate/`, 'POST',
            { ...payload, turn_id: turnId },
        );
        if (wanted !== threadIdRef.current) return { ok: false };
        if (res?.ok) {
            setError('');
            setState(stamp(res.data));
            return { ok: true, cleared: !payload?.verdict };
        }
        // 409 means the reply moved on underneath the popover: the AI answered
        // again, or the mode changed. Saying "that did not save" would be true
        // but useless, so take the corrected reply the server sent back.
        //
        // `ratingKey` is bumped so TurnRating remounts. Without it the tags and
        // the comment typed against the OLD reply stay in its local state — it
        // re-seeds on `mine.id`, and both turns are unrated, so undefined ->
        // undefined does not fire the effect — and the next click would post
        // "wrong tone" against a reply nobody assessed for it.
        if (res?.status === 409) {
            const fresh = res?.data?.rateable;
            setState((prev) => (prev && fresh ? { ...prev, rateable: fresh } : prev));
            setRatingKey((n) => n + 1);
            setError('The AI has replied again since. Rate the newer reply.');
            return { ok: false };
        }
        setError('That rating did not save.');
        return { ok: false };
    };

    // ---- the draft goes to the composer -------------------------------
    //
    // It used to live in this popover, behind a button, at the top of the
    // screen. So the reply the AI wrote appeared nowhere near the box the
    // operator types in, and using it meant opening a panel, reading it,
    // editing it in a second textarea and pressing a second Send — while the
    // real composer sat empty underneath, inviting them to type the reply
    // again from scratch. Most people did.
    //
    // Now the draft is published upward and lands in the composer itself,
    // where a reply is written. This component keeps the turn id and the
    // send/discard calls, because sending through `send-draft` is what marks
    // the turn resolved and attributes the message to the AI — a plain send
    // from the composer would go out as a human reply and the turn would sit
    // PENDING forever.
    const pendingId = state?.pending_draft?.id ?? null;
    const pendingText = state?.pending_draft?.text || '';
    const agentBusy = live?.state === 'queued' || live?.state === 'composing';
    const visible = !!state && state.practice_mode !== 'OFF';

    useEffect(() => {
        if (!onDraftState) return undefined;
        if (!visible) {
            onDraftState(null);
            return undefined;
        }
        onDraftState({
            threadId,
            composing: agentBusy,
            draft: pendingId ? { id: pendingId, text: pendingText } : null,
            send: (text) => resolveDraft('send-draft', text),
            discard: () => resolveDraft('discard-draft'),
            // R1b at chat level. The server only offers this in LIVE — the
            // mode where the AI spoke to the patient without anyone checking
            // first — so this strip appears exactly when there is a reply
            // nobody approved and a colleague is standing in front of it.
            // It was already in the popover, behind a button nobody clicks
            // once the draft stopped living there.
            rateable: state?.rateable || null,
            // The failure vocabulary, from the server, present whether or not
            // the thread has a "latest rateable turn". It used to hang off
            // `rateable`, so the dialog opened with the sentence "the tags are
            // what add up across replies" above no tags at all.
            ratingTags: state?.rating_tags || [],
            rate: (payload) => rate(payload),
        });
        // Nothing of this thread's draft may survive a thread switch or an
        // unmount — the composer would otherwise keep offering to send a turn
        // id that belongs to a conversation nobody is looking at.
        return () => onDraftState(null);
        // resolveDraft is recreated every render; the deps below are what
        // actually change what it would do.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onDraftState, visible, threadId, pendingId, pendingText, agentBusy,
        state?.rating_tags?.length,
        state?.rateable?.turn_id, state?.rateable?.mine?.id]);

    const change = async (mode) => {
        if (!mode || !threadId || !practiceId) return;
        setBusy(true);
        try {
            const res = await callApi(`${base(practiceId)}/${threadId}/set-mode/`, 'POST', { mode });
            if (res?.ok) {
                setError('');
                setState(stamp(res.data));
                return;
            }
            setError('That change did not save.');
            load();
        } finally {
            setBusy(false);
        }
    };

    // SHADOW arrives here as OFF for anyone but a superuser — the server
    // decides that (WhatsappAIThreadControlViewSet._visible_state), so the
    // practice cannot find the agent in the network tab either.
    if (!state || state.practice_mode === 'OFF') return null;

    const activity = activityOf(state);
    const overridden = state.ai_mode !== 'INHERIT'
        && state.effective_mode !== state.practice_mode;
    const hasDraft = !!state.pending_draft;
    const open = !!anchorEl;
    const iconTooltip = hasDraft
        ? 'AI Bot — a reply is waiting for you'
        : `AI Bot — ${activity.text}`;
    const working = live?.state === 'queued' || live?.state === 'composing';
    const liveLabel = (() => {
        if (live?.state === 'composing') return 'AI is writing…';
        if (live?.state === 'queued') {
            const verb = live.mode === 'DRAFT' ? 'draft' : 'reply';
            return countdown > 0
                ? `AI will ${verb} in ${countdown}s`
                : `AI is about to ${verb}`;
        }
        return null;
    })();

    return (
        <>
            <Stack direction="row" alignItems="center" spacing={0.75}>
                {/* The countdown is the point: the reply delay exists to give a
                    colleague first refusal, so they have to be able to see the
                    clock they are racing. */}
                {liveLabel && (
                    <Chip
                        size="small"
                        variant="outlined"
                        color={live.state === 'composing' ? 'info' : 'default'}
                        icon={(
                            <Iconify
                                icon={live.state === 'composing'
                                    ? 'svg-spinners:3-dots-fade'
                                    : 'solar:clock-circle-bold-duotone'}
                            />
                        )}
                        label={liveLabel}
                        sx={{ height: 24, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
                    />
                )}
                <Tooltip
                    arrow
                    // Silent while the panel is open. The tooltip exists to say
                    // what the icon means before you click it; once the panel is
                    // up it repeats the state chip three lines below, and the
                    // popper physically overlaps the thing it is describing.
                    //
                    // It also made the panel's own tests fail. Clicking the
                    // button to open the panel focuses it, an MUI tooltip opens
                    // on focus, and `getByText(/replying automatically/i)` then
                    // matched both the tooltip and the panel chip —
                    // "found multiple elements", five tests at once, none of
                    // them about tooltips. It only bites when the machine is
                    // slow enough for the tooltip to appear before the
                    // assertion runs, which is why it failed in CI and passed
                    // locally. An empty title renders no popper at all.
                    title={open ? '' : iconTooltip}
                >
                    <Badge
                        color={hasDraft ? 'warning' : activity.dot || 'default'}
                        variant="dot"
                        invisible={!hasDraft && !activity.dot}
                        overlap="circular"
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <IconButton
                            size="small"
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            sx={{
                                ...iconSx,
                                ...(open && { backgroundColor: 'rgba(145, 158, 171, 0.12)' }),
                            }}
                            aria-label="AI Bot"
                        >
                            <Iconify
                                icon="solar:magic-stick-3-bold-duotone"
                                width={18} height={18}
                                sx={working ? {
                                    // Declared inline: there is no global `pulse`
                                    // keyframe, and CSS ignores an unknown
                                    // animation-name silently — so naming one
                                    // without defining it animates nothing and
                                    // reports nothing.
                                    '@keyframes aiPulse': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.45 },
                                    },
                                    animation: 'aiPulse 1.4s ease-in-out infinite',
                                } : undefined}
                            />
                        </IconButton>
                    </Badge>
                </Tooltip>
            </Stack>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { width: 360, maxWidth: '95vw', mt: 0.75, borderRadius: 2 } }}
            >
                {/* One state, said once. The old header showed the configured
                    mode as the headline and the exceptions as small chips
                    beside it, so a handed-off conversation read "Replying
                    automatically" and "Handed to you" at the same time — two
                    badges contradicting each other with the true one smaller. */}
                <Box sx={{ p: 2, pb: 1.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Iconify icon="solar:magic-stick-3-bold-duotone" width={20} height={20} />
                        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>AI Bot</Typography>
                        <Chip
                            size="small"
                            label={activity.text}
                            color={activity.colour}
                            sx={{ fontWeight: 600 }}
                        />
                    </Stack>

                    {activity.help && (
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.75, lineHeight: 1.45 }}
                        >
                            {activity.help}
                        </Typography>
                    )}

                    {/* What it is doing this second. Outside the popover this is
                        a chip on the toolbar; inside, it is the answer to the
                        question somebody opened the panel to ask — and in Draft
                        mode it is the only way to know a reply is coming rather
                        than nothing being wired up. */}
                    {liveLabel && (
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 1 }}>
                            <Iconify
                                icon={live.state === 'composing'
                                    ? 'svg-spinners:3-dots-fade'
                                    : 'solar:clock-circle-bold-duotone'}
                                width={16}
                                height={16}
                            />
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {liveLabel}
                            </Typography>
                        </Stack>
                    )}

                    {overridden && (
                        <Tooltip
                            arrow
                            title={`The practice is set to ${state.practice_mode}. The stricter of the two applies.`}
                        >
                            <Chip
                                size="small"
                                variant="outlined"
                                label="Set differently here"
                                sx={{ mt: 1, height: 22, fontSize: 11 }}
                            />
                        </Tooltip>
                    )}
                </Box>

                {error && (
                    <Alert
                        severity="error"
                        onClose={() => setError('')}
                        sx={{ borderRadius: 0 }}
                    >
                        {error}
                    </Alert>
                )}

                {/* The draft is in the composer. This is a pointer to it, not
                    a second copy of it — two editable boxes holding the same
                    reply is how an operator sends the version they did not
                    edit. Kept as a fallback path too: if the parent screen does
                    not take the draft (an embedded chat, an older build), these
                    buttons still resolve the turn. */}
                {hasDraft && (
                    <>
                        <Divider />
                        <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.neutral' }}>
                            <Stack spacing={1}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Iconify icon="solar:pen-new-square-bold-duotone" />
                                    <Typography variant="subtitle2">
                                        {onDraftState ? 'Reply ready in your message box' : 'Suggested reply'}
                                    </Typography>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    {onDraftState
                                        ? 'It is typed into the composer below. Edit it there and press Send — nothing goes until you do.'
                                        : 'Edit it if you like — nothing sends until you say so.'}
                                </Typography>
                                {!onDraftState && (
                                    <TextField
                                        multiline
                                        minRows={3}
                                        fullWidth
                                        size="small"
                                        value={draftText}
                                        onChange={(e) => setDraftText(e.target.value)}
                                    />
                                )}
                                <Stack direction="row" spacing={1}>
                                    {!onDraftState && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={busy || !draftText.trim()}
                                            onClick={() => resolveDraft('send-draft')}
                                        >
                                            Send
                                        </Button>
                                    )}
                                    <Button
                                        size="small"
                                        color="inherit"
                                        disabled={busy}
                                        onClick={() => resolveDraft('discard-draft')}
                                    >
                                        Discard
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>
                    </>
                )}

                {state.conversation_state === 'HANDOFF' && (
                    <>
                        <Divider />
                        <Box sx={{ px: 2, py: 1.5 }}>
                            <Button
                                size="small"
                                variant="outlined"
                                disabled={busy}
                                startIcon={<Iconify icon="solar:restart-bold" />}
                                // Re-applying the current mode is what releases the
                                // handoff server-side — except PAUSED, which would
                                // pause it instead. A button labelled "Let the AI
                                // continue" that pauses the AI is worse than no
                                // button, so a paused thread goes back to following
                                // the practice.
                                onClick={() => change(
                                    !state.ai_mode || state.ai_mode === 'PAUSED'
                                        ? 'INHERIT'
                                        : state.ai_mode,
                                )}
                            >
                                Let the AI continue
                            </Button>
                        </Box>
                    </>
                )}

                <Divider />

                {/* A setting, labelled as one. Unlabelled, the four rows read as
                    four more status lines under the status line — which is why
                    "Auto-send: it replies on its own" sat under "Replying
                    automatically" saying the same thing twice.

                    One line each. The old list gave every row a second line of
                    explanation, so a panel that also has to hold a draft and a
                    rating opened with eight lines of prose about settings
                    nobody was changing. The explanation is kept for the row
                    that is actually selected, where it describes what is
                    happening, and on hover for the rest, where it describes
                    what would happen. */}
                <Box sx={{ py: 0.5 }}>
                    <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ px: 2, fontSize: 10, letterSpacing: 0.6 }}
                    >
                        How it behaves here
                    </Typography>
                    {MODES.map((m) => {
                        const selected = state.ai_mode === m.value;
                        return (
                            <Tooltip key={m.value} arrow placement="left" title={selected ? '' : m.help}>
                                <MenuItem
                                    selected={selected}
                                    disabled={busy}
                                    onClick={() => change(m.value)}
                                    sx={{ py: 0.75, alignItems: 'center' }}
                                >
                                    <Iconify
                                        icon={m.icon}
                                        width={18}
                                        height={18}
                                        sx={{ mr: 1.5, color: selected ? 'primary.main' : 'text.secondary' }}
                                    />
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: selected ? 600 : 400 }}
                                        >
                                            {m.label}
                                        </Typography>
                                        {selected && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.4 }}
                                            >
                                                {m.help}
                                            </Typography>
                                        )}
                                    </Box>
                                    {selected && (
                                        <Iconify
                                            icon="eva:checkmark-fill"
                                            width={18}
                                            height={18}
                                            sx={{ ml: 1, color: 'primary.main' }}
                                        />
                                    )}
                                </MenuItem>
                            </Tooltip>
                        );
                    })}
                </Box>

                {/* Only when the server offers one, which is LIVE only: it is
                    the mode where the AI spoke to the patient without anyone
                    checking first, so a colleague's verdict is the only outside
                    reference the system has. In Draft they already approved the
                    wording by sending it, and grading that grades themselves.

                    Below the draft box on purpose — an unanswered draft is the
                    one thing here that needs doing now, and putting a question
                    about an already-sent reply above it gets it missed. */}
                {state.rateable && (
                    <>
                        <Divider />
                        <Box sx={{ px: 2, py: 1.5 }}>
                            {!!state.rateable.text && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        fontStyle: 'italic',
                                        mb: 1,
                                    }}
                                >
                                    {/* What is being rated. Without it, a
                                        colleague opening a conversation the AI
                                        handled while they were away is asked to
                                        judge a reply they have not read. */}
                                    {`“${state.rateable.text}”`}
                                </Typography>
                            )}
                            <TurnRating
                                key={`${state.rateable.turn_id}:${ratingKey}`}
                                compact
                                turn={{ ratings: { mine: state.rateable.mine, all: [] } }}
                                tagOptions={state.rateable.tag_options || []}
                                onRate={rate}
                            />
                        </Box>
                    </>
                )}
            </Popover>
        </>
    );
}
